import { imageAccessAPI } from './api';
import {
  jitterMsForStableKey,
  staggerDelayMsForKey,
  ttlMsWithStableJitter,
} from './cacheExpiryJitter';

const displayCache = new Map();
/** 按图片 id 缓存已解析展示地址，卡片 remount 时可同步命中 */
const imageIdDisplayCache = new Map();
const DISPLAY_CACHE_STORAGE_KEY = 'ipe:managed-image-display-cache';
const DISPLAY_CACHE_LOCAL_STORAGE_KEY = 'ipe:managed-image-display-cache:v1';
const DISPLAY_CACHE_STORAGE_MAX = 800;
const DISPLAY_CACHE_MAINTENANCE_INTERVAL_MS = 5 * 60 * 1000;
const DISPLAY_CACHE_MAINTENANCE_JITTER_MS = 2 * 60 * 1000;
const VIEW_REFRESH_LEAD_BASE_MS = 60_000;
const VIEW_REFRESH_LEAD_JITTER_MS = 5 * 60 * 1000;
/** 无 Expires 的公网直链：缓存写入时锚定的默认寿命（与 viewTtl 默认 14400s 对齐） */
export const DISPLAY_CACHE_DEFAULT_TTL_MS = 14400 * 1000;
const cacheUpdateListeners = new Set();
let persistStorageTimer = null;
const SIGN_BATCH_MAX = 100;

/** 与后端 ImageViewVariant.ossProcessFor 对齐 */
export const IMAGE_VIEW_OSS_PROCESS = {
  thumb: 'image/resize,w_256,h_256,m_lfit/quality,q_85',
  preview: 'image/resize,w_512,h_512,m_lfit/quality,q_88',
  medium: 'image/resize,w_1024,h_1024,m_lfit/quality,q_90',
};

export function normalizeImageViewVariant(variant) {
  const v = String(variant || '').trim().toLowerCase();
  if (!v || v === 'full') return '';
  if (v === 'thumb' || v === 'preview' || v === 'medium') return v;
  return '';
}

/** 从 x-oss-process 反查 thumb|preview|medium；未知 process 返回空串 */
export function variantFromOssProcess(processStyle) {
  const process = String(processStyle || '').trim();
  if (!process) return '';
  return Object.entries(IMAGE_VIEW_OSS_PROCESS)
    .find(([, style]) => style === process)?.[0] || '';
}

/**
 * 将签名 view / OSS 直链 / 绝对 uploads 还原为 canonical `/uploads/{key}`。
 * 带 x-oss-process 的 OSS URL 也会剥掉 process，避免误当 canonical。
 */
export function toManagedCanonicalUploadsUrl(url) {
  const s = normalizeUrlPath(url);
  if (!s) return '';
  if (s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) {
    return '';
  }
  if (isSignedViewPath(s)) {
    try {
      const parsed = s.startsWith('http://') || s.startsWith('https://')
        ? new URL(s)
        : new URL(s, 'http://local');
      const key = parsed.searchParams.get('k');
      if (!key) return '';
      const decoded = decodeURIComponent(key).replace(/\\/g, '/').replace(/^\/+/, '');
      if (!decoded || decoded.includes('..') || !isManagedStorageUploadPath(decoded)) return '';
      return `/uploads/${decoded}`;
    } catch {
      return '';
    }
  }
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const parsed = new URL(s);
      if (parsed.pathname.startsWith('/uploads/') || parsed.pathname.startsWith('/api/uploads/')) {
        const key = stripUploadPrefix(parsed.pathname);
        return key && isManagedStorageUploadPath(key) ? `/uploads/${key}` : '';
      }
      // OSS 桶直链 / 自定义 CDN：pathname 即 object key
      const key = parsed.pathname.replace(/^\//, '');
      if (key && isManagedStorageUploadPath(key)) return `/uploads/${key}`;
    } catch {
      return '';
    }
    return '';
  }
  if (isManagedStorageUploadPath(s) || isUploadsStoragePath(s)) {
    const key = stripUploadPrefix(s.split('?')[0]);
    return key && isManagedStorageUploadPath(key) ? `/uploads/${key}` : '';
  }
  return '';
}

/**
 * 签名展示 URL 内嵌的 variant：HMAC `v=` 或 OSS `x-oss-process`。
 * 有未知 process 时返回 `'__process__'`，表示「非原图」不可当 full。
 */
export function embeddedSignedDisplayVariant(url) {
  const s = normalizeUrlPath(url);
  if (!s) return '';
  if (isSignedViewPath(s)) {
    try {
      const parsed = s.startsWith('http://') || s.startsWith('https://')
        ? new URL(s)
        : new URL(s, 'http://local');
      return normalizeImageViewVariant(parsed.searchParams.get('v'));
    } catch {
      return '';
    }
  }
  if (!s.startsWith('http://') && !s.startsWith('https://')) return '';
  try {
    const parsed = new URL(s);
    if (!isOssDisplayHost(parsed)) return '';
    const process = parsed.searchParams.get('x-oss-process') || '';
    if (!process) return '';
    return variantFromOssProcess(process) || '__process__';
  } catch {
    return '';
  }
}

/**
 * 已是可直出的签名地址，且内嵌 variant 与请求一致时才可 skip re-sign。
 * 带 process 的 thumb OSS 在无 variant（full）请求下必须重新签发。
 */
export function isReadySignedDisplayForVariant(url, requestedVariant) {
  const s = normalizeUrlPath(url);
  if (!s || !isSignedDisplayUrl(s) || isViewPathExpired(s)) return false;
  const want = normalizeImageViewVariant(requestedVariant);
  const embedded = embeddedSignedDisplayVariant(s);
  if (embedded === '__process__') return false;
  return embedded === want;
}

function encodeSignQueueEntry(url, variant) {
  const v = normalizeImageViewVariant(variant);
  return v ? `${url}\0${v}` : url;
}

function decodeSignQueueEntry(entry) {
  const raw = String(entry || '');
  const idx = raw.indexOf('\0');
  if (idx < 0) return { url: raw, variant: '' };
  return { url: raw.slice(0, idx), variant: raw.slice(idx + 1) };
}

/** 合并同一事件循环内多处 ensureDisplayImageUrls 调用，避免 N 张图 N 次 sign-batch */
const signCoalesce = {
  queue: new Set(),
  waiters: [],
  timer: null,
  inflight: null,
};

/** 与后端 ManagedStorageKeyPolicy.KNOWN_ROOTS 同步 */
export const MANAGED_UPLOAD_ROOTS = [
  'image-space',
  'image-space-team',
  'image-ipe-agent',
  'img2img',
  'infringement',
  'goods-template',
  'listing-export',
  'workflow-export',
  'fission',
  'avatars',
  'system',
  'shein-certificate-library',
  'shein-photo-template',
  'shein-product-template',
  'temu-certificate-library',
  'temu-photo-template',
  'temu-product-template',
];

/** 后端 LEGACY_ROOTS：仍走 sign-batch，object key 会映射到 KNOWN_ROOTS */
const LEGACY_MANAGED_UPLOAD_ROOTS = ['official', 'agent', 'funding'];

const MANAGED_ROOT_SET = new Set([...MANAGED_UPLOAD_ROOTS, ...LEGACY_MANAGED_UPLOAD_ROOTS]);

function normalizeUrlPath(url) {
  return String(url || '').trim().replace(/\\/g, '/');
}

function stripUploadPrefix(path) {
  let s = normalizeUrlPath(path);
  while (s.startsWith('/')) {
    s = s.slice(1);
  }
  if (s.startsWith('api/uploads/')) {
    return s.slice('api/uploads/'.length);
  }
  if (s.startsWith('uploads/')) {
    return s.slice('uploads/'.length);
  }
  return s;
}

function firstUploadRoot(pathOrKey) {
  const key = stripUploadPrefix(pathOrKey);
  if (!key) return '';
  const slash = key.indexOf('/');
  return slash < 0 ? key : key.slice(0, slash);
}

function isManagedUploadRoot(root) {
  return Boolean(root) && MANAGED_ROOT_SET.has(root);
}

function isManagedStorageUploadPath(path) {
  return isManagedUploadRoot(firstUploadRoot(path));
}

/** 任意 /uploads/ 或 /api/uploads/ 路径（含非白名单 legacy 如 funding） */
function isUploadsStoragePath(url) {
  if (!url || typeof url !== 'string') return false;
  const s = normalizeUrlPath(url);
  if (!s) return false;
  if (s.startsWith('/uploads/') || s.startsWith('uploads/')) return true;
  if (s.startsWith('/api/uploads/') || s.startsWith('api/uploads/')) return true;
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const p = new URL(s).pathname;
      return p.startsWith('/uploads/') || p.startsWith('/api/uploads/');
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * 展示地址策略（local / OSS 共用，禁止按环境改来改去）：
 *
 * 可写入 &lt;img src&gt;：
 * 1. HMAC `/api/storage/images/view?…`
 * 2. OSS 预签名直链（带 Expires/Signature 或 V4 x-oss-*）
 * 3. 绝对 `http(s)://…/uploads/…`（local disk / legacy identity）
 *
 * 禁止（signed-only）：
 * - 相对裸 `/uploads/…`（须先 sign-batch）
 * - 未预签名的受管 OSS 公网裸链（私有桶会 403）
 *
 * 持久化仍只用 canonical `/uploads/{key}`；展示形态一律以 sign-batch / viewUrl 为准。
 */
function shouldBlockBareUploadsDirectLink(url) {
  const s = normalizeUrlPath(url);
  if (!s) return false;
  // 本地/legacy：绝对 uploads 直链可作 img src（生产 signed-only 通常不会签发这种形态）
  if ((s.startsWith('http://') || s.startsWith('https://')) && isUploadsStoragePath(s)) {
    return false;
  }
  return isSignedViewEnabled() && isUploadsStoragePath(s) && !isSignedViewPath(s);
}

/**
 * signed-only 下不可作展示源的 http(s)：主要是未预签名的受管 OSS 裸链。
 * 注意：绝对 `/uploads/` 也是 isManagedStorageUrl，但属于 local 合法形态，必须排除。
 */
function isBlockedSignedOnlyHttpDisplay(url) {
  if (!isSignedViewEnabled()) return false;
  const s = normalizeUrlPath(url);
  if (!s.startsWith('http://') && !s.startsWith('https://')) return false;
  if (isSignedViewPath(s) || isOssDirectDisplayUrl(s)) return false;
  if (isUploadsStoragePath(s)) return false;
  return isManagedStorageUrl(s);
}

/** 本地 disk / legacy：已是可加载的绝对 uploads 直链 */
function isAbsoluteLocalUploadsDisplayUrl(url) {
  const s = normalizeUrlPath(url);
  return Boolean(
    s
    && (s.startsWith('http://') || s.startsWith('https://'))
    && isUploadsStoragePath(s),
  );
}

/** REACT_APP_IMAGE_SIGNED_VIEW=false 时可回退公网 /uploads/ 直链，仅用于 local dev 紧急调试 */
export function isSignedViewEnabled() {
  const raw = String(process.env.REACT_APP_IMAGE_SIGNED_VIEW ?? 'true').trim().toLowerCase();
  return raw !== 'false' && raw !== '0' && raw !== 'no';
}

function isManagedStorageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const s = normalizeUrlPath(url);
  if (!s) return false;
  if (s.startsWith('/api/storage/images/view')) return true;
  if (isManagedStorageUploadPath(s)) return true;
  if (!s.startsWith('http://') && !s.startsWith('https://')) return false;
  try {
    const parsed = new URL(s);
    if (isManagedStorageUploadPath(parsed.pathname)) return true;
    const host = parsed.hostname.toLowerCase();
    if (host.includes('.oss-') && host.includes('aliyuncs.com')) {
      const key = parsed.pathname.replace(/^\//, '');
      return isManagedStorageUploadPath(key);
    }
  } catch {
    return false;
  }
  return false;
}

/** @deprecated 使用 isManagedStorageUrl；保留 alias 便于渐进迁移 */
const isOssManagedUrl = isManagedStorageUrl;

const CANONICAL_DISPLAY_FIELD_MAP = {
  url: ['displayUrl', 'viewUrl', 'urlDisplay'],
  primaryImageUrl: ['primaryImageViewUrl', 'primaryImageUrlDisplay'],
  initialImageUrl: ['initialImageUrlDisplay'],
  finalImageUrl: ['finalImageUrlDisplay'],
  outputImageUrl: ['outputImageUrlDisplay', 'viewUrl', 'displayUrl'],
  inputImageUrl: ['inputImageUrlDisplay'],
  outputVideoUrl: ['outputVideoUrlDisplay'],
  videoUrl: ['videoUrlDisplay'],
  outputVideoPosterUrl: ['outputVideoPosterUrlDisplay'],
  previewUrl: ['previewDisplayUrl', 'previewViewUrl', 'previewUrlDisplay'],
  sourceUrl: ['sourceUrlDisplay'],
};

function urlsLooselySame(a, b) {
  if (!a || !b) return false;
  const sa = normalizeUrlPath(a);
  const sb = normalizeUrlPath(b);
  if (sa === sb) return true;
  return sa.split('?')[0] === sb.split('?')[0];
}

function stripUrlQueryForMatch(url) {
  let s = normalizeUrlPath(url);
  if (!s) return '';
  if (s.match(/^https?:\/\//)) {
    const idx = s.indexOf('/uploads/');
    if (idx >= 0) s = s.substring(idx);
  }
  const q = s.indexOf('?');
  if (q >= 0) s = s.substring(0, q);
  return s;
}

/** 从 API enrich 后的 record 中，按 canonical URL 查找对应 viewUrl/displayUrl */
export function resolveSignedDisplayForUrl(rawUrl, record) {
  if (!rawUrl || !record || typeof record !== 'object') return '';
  const raw = stripUrlQueryForMatch(rawUrl);
  if (!raw) return '';
  for (const [field, displayFields] of Object.entries(CANONICAL_DISPLAY_FIELD_MAP)) {
    if (!urlsLooselySame(record[field], raw)) continue;
    for (const displayField of displayFields) {
      const value = record[displayField];
      if (value) return toAbsoluteViewPath(value);
    }
  }
  const parallelLists = [
    ['compositeImageUrls', 'compositeImageViewUrls'],
    ['imageUrls', 'imageViewUrls'],
  ];
  for (const [urlField, viewField] of parallelLists) {
    const urls = record[urlField];
    const views = record[viewField];
    if (!Array.isArray(urls) || !Array.isArray(views)) continue;
    const idx = urls.findIndex((u) => urlsLooselySame(stripUrlQueryForMatch(u), raw));
    if (idx >= 0 && views[idx]) return toAbsoluteViewPath(views[idx]);
  }
  return '';
}

/** 从列表/API 条目中取出 sign-batch 与 resolve 用的 viewUrl 字段 */
export function pickImageSignOptions(record) {
  if (!record || typeof record !== 'object') return {};
  return {
    displayUrl: record.displayUrl,
    urlDisplay: record.urlDisplay,
    viewUrl: record.viewUrl,
    storedUrlDisplay: record.storedUrlDisplay,
    storedViewUrl: record.storedViewUrl,
    primaryImageUrlDisplay: record.primaryImageUrlDisplay,
    primaryImageViewUrl: record.primaryImageViewUrl,
    initialImageUrlDisplay: record.initialImageUrlDisplay,
    finalImageUrlDisplay: record.finalImageUrlDisplay,
    outputImageUrlDisplay: record.outputImageUrlDisplay,
    inputImageUrlDisplay: record.inputImageUrlDisplay,
    outputVideoUrlDisplay: record.outputVideoUrlDisplay,
    videoUrlDisplay: record.videoUrlDisplay,
    outputVideoPosterUrlDisplay: record.outputVideoPosterUrlDisplay,
    previewDisplayUrl: record.previewDisplayUrl,
    previewViewUrl: record.previewViewUrl,
  };
}

/**
 * 从列表条目中收集需 sign-batch 的 canonical URL（跳过 API 已 sign 与本地 URL）。
 * @param {Array} items
 * @param {(item: object) => string} [resolveRawUrl]
 */
export function collectManagedDisplayUrls(items, resolveRawUrl = (item) => item?.url || item?.src || '') {
  const urls = [];
  for (const item of items || []) {
    if (
      item?.displayUrl
      || item?.viewUrl
      || item?.storedUrlDisplay
      || item?.storedViewUrl
    ) {
      continue;
    }
    const raw = String(resolveRawUrl(item) || '').trim();
    if (!raw || raw.startsWith('data:') || raw.startsWith('blob:') || raw.startsWith('indexeddb://')) {
      continue;
    }
    if (!isManagedStorageUrl(raw)) continue;
    // 勿把带 process 的 OSS 直链当 canonical 送 sign-batch
    urls.push(toManagedCanonicalUploadsUrl(raw) || raw);
  }
  return [...new Set(urls)];
}

function resolveApiBaseUrl() {
  const raw = (process.env.REACT_APP_API_URL || '').trim();
  // 同域 Nginx 反代时 REACT_APP_API_URL=/api；签名读图路径已含 /api/…，勿再拼前缀
  if (raw.startsWith('/')) return '';
  return raw.replace(/\/api\/?$/, '').replace(/\/$/, '');
}

function toAbsoluteViewPath(path) {
  const p = String(path || '').trim();
  if (!p) return '';
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  const base = resolveApiBaseUrl();
  if (!base) return p;
  return `${base}${p.startsWith('/') ? '' : '/'}${p}`;
}

/** 后端 legacy/signed-only=false 时 sign-batch 可能回传裸 canonical；转为可加载的绝对直链 */
function resolvePublicManagedUploadsUrl(canonical) {
  const raw = normalizeUrlPath(canonical);
  if (!raw || !isManagedStorageUrl(raw)) return '';
  let pub = (process.env.REACT_APP_PUBLIC_BASE_URL || '').trim().replace(/\/$/, '');
  if (!pub) {
    pub = resolveApiBaseUrl();
  }
  if (!pub && typeof window !== 'undefined' && window.location?.origin) {
    pub = String(window.location.origin).replace(/\/$/, '');
  }
  if (!pub) return raw;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${pub}${path}`;
}

/** 解析 sign-batch 单条结果：签名 view、OSS 预签名，或 legacy 裸 /uploads/（转绝对直链） */
function resolveSignBatchDisplayUrl(canonical, batchViewPath) {
  const view = String(batchViewPath || '').trim();
  if (!view) return '';
  if (isSignedViewPath(view)) return toAbsoluteViewPath(view);
  if (view.startsWith('http://') || view.startsWith('https://')) {
    // signed-only：裸 OSS 不可入缓存；绝对 /uploads/（local）与预签名 OSS 可入
    if (isBlockedSignedOnlyHttpDisplay(view)) return '';
    return view;
  }
  // signed-only=false / 异常透传：value 为相对 /uploads/…（不必与 request key 字面相等）
  const normalizedView = normalizeUrlPath(view);
  if (isUploadsStoragePath(normalizedView) || isManagedStorageUrl(normalizedView)) {
    return resolvePublicManagedUploadsUrl(normalizedView) || normalizedView;
  }
  const raw = normalizeUrlPath(canonical);
  if (raw && normalizedView === raw && isManagedStorageUrl(raw)) {
    return resolvePublicManagedUploadsUrl(raw) || raw;
  }
  return '';
}

function isOssAliyunHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h.includes('aliyuncs.com')) return false;
  return h.includes('.oss-')
    || h.includes('oss-accelerate')
    || h.endsWith('.oss.aliyuncs.com');
}

/** OSS 预签名查询参数（V1 Expires/Signature，或 V4 x-oss-*） */
function hasOssPresignQuery(parsed) {
  if (!parsed?.searchParams) return false;
  const p = parsed.searchParams;
  if (
    (p.has('Expires') || p.has('expires'))
    && (p.has('Signature') || p.has('signature') || p.has('OSSAccessKeyId'))
  ) {
    return true;
  }
  return Boolean(
    p.get('x-oss-signature-version')
    || p.get('x-oss-credential')
    || p.get('x-oss-signature')
    || p.get('X-Oss-Signature'),
  );
}

/**
 * 默认 OSS 域名，或自定义域名/CDN（须带 OSS 预签名，避免误伤外站 https）。
 */
function isOssDisplayHost(parsed) {
  if (!parsed?.hostname) return false;
  if (isOssAliyunHost(parsed.hostname)) return true;
  return hasOssPresignQuery(parsed);
}

/**
 * OSS 客户端直链：client-direct-url 开启后 sign-batch 的预签名产出。
 * signed-only 下未带预签名参数的私有桶裸链不可直出（否则 &lt;img&gt; 会 403）。
 * 自定义域名（如 image.ycipe.cn）同样须识别，否则会走 isBlockedSignedOnlyHttpDisplay 被整页拦掉。
 */
export function isOssDirectDisplayUrl(url) {
  const s = normalizeUrlPath(url);
  if (!s.startsWith('http://') && !s.startsWith('https://')) return false;
  try {
    const parsed = new URL(s);
    const key = parsed.pathname.replace(/^\//, '');
    if (!isManagedStorageUploadPath(key)) return false;
    if (!isOssDisplayHost(parsed)) return false;
    if (isSignedViewEnabled() && !hasOssPresignQuery(parsed)) return false;
    return true;
  } catch {
    return false;
  }
}

/** 可写入 &lt;img&gt; 的签名展示地址：HMAC view 或 OSS 直链 */
export function isSignedDisplayUrl(url) {
  return isSignedViewPath(url) || isOssDirectDisplayUrl(url);
}

export function isSignedViewPath(url) {
  const s = normalizeUrlPath(url);
  if (!s) return false;
  if (s.startsWith('/api/storage/images/view')) return true;
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      return new URL(s).pathname.startsWith('/api/storage/images/view');
    } catch {
      return false;
    }
  }
  return false;
}

/** 是否可直接作为 displayUrl/viewUrl 写入 img（已签名 view、OSS 预签名、公网直链、data/blob；同域 /uploads/ 亦可） */
export function isLoadableDisplayPreviewUrl(url) {
  const s = normalizeUrlPath(String(url || '').trim());
  if (!s) return false;
  if (s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) {
    return true;
  }
  if (isSignedDisplayUrl(s)) {
    return !isViewPathExpired(s);
  }
  if (s.startsWith('http://') || s.startsWith('https://')) {
    if (shouldBlockBareUploadsDirectLink(s)) return false;
    // 裸 OSS 拦截；绝对 /uploads/（local）放行
    if (isBlockedSignedOnlyHttpDisplay(s)) return false;
    return true;
  }
  // 同域相对 /uploads/：可挂 <img>（dev proxy / local disk）；勿因「须先 sign」永久白底无请求
  if (s.startsWith('/uploads/') || s.startsWith('/api/uploads/')) {
    return true;
  }
  return false;
}

/** 列表缩略图变体：thumb | preview | medium | full（默认不加参） */
export function appendViewVariant(viewUrl, variant) {
  const raw = String(viewUrl || '').trim();
  const v = normalizeImageViewVariant(variant);
  if (!raw || !v) return raw;
  // OSS 预签名须在签发时带 x-oss-process，不可事后追加
  if (isOssDirectDisplayUrl(raw)) return raw;
  if (!isSignedViewPath(raw)) return raw;
  try {
    const parsed = raw.startsWith('http://') || raw.startsWith('https://')
      ? new URL(raw)
      : new URL(raw, 'http://local');
    if (parsed.searchParams.get('v') === v) {
      return raw;
    }
    parsed.searchParams.set('v', v);
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return raw;
  }
}

function pickPreferredDisplayField(options = {}) {
  return (
    options.displayUrl
    || options.urlDisplay
    || options.viewUrl
    || options.storedUrlDisplay
    || options.storedViewUrl
    || options.primaryImageUrlDisplay
    || options.primaryImageViewUrl
    || options.initialImageUrlDisplay
    || options.finalImageUrlDisplay
    || options.outputImageUrlDisplay
    || options.inputImageUrlDisplay
    || options.outputVideoUrlDisplay
    || options.videoUrlDisplay
    || options.outputVideoPosterUrlDisplay
    || options.previewDisplayUrl
    || options.previewViewUrl
    || ''
  );
}

/**
 * 用 API 已下发的 full viewUrl 派生列表 thumb，避免每卡再打 sign-batch。
 * - HMAC `/view`：追加 v=thumb（后端按变体处理）
 * - OSS 直链：无法事后加 process；直接复用已有 full 直链做首屏，真·缩略图由列表 prefetch 后台升级
 */
export function deriveVariantDisplayFromPreferred(preferred, variant) {
  const safePreferred = pickSafePreferredDisplaySrc(preferred);
  if (!safePreferred) return '';
  // blob/data 本地预览与 variant 无关，不可因请求 thumb 被清空（否则大批量上传后全走签图易 429 裂图）
  if (
    safePreferred.startsWith('data:')
    || safePreferred.startsWith('blob:')
    || safePreferred.startsWith('indexeddb://')
  ) {
    return safePreferred;
  }
  if (safePreferred.startsWith('/images/') || safePreferred.startsWith('/assets/')) {
    return safePreferred;
  }
  const v = normalizeImageViewVariant(variant);
  if (!v) return safePreferred;
  if (isSignedViewPath(safePreferred)) {
    return appendViewVariant(safePreferred, v) || safePreferred;
  }
  if (isOssDirectDisplayUrl(safePreferred)) {
    // 已是目标 variant（带 process）则直出；否则先用 full 占位，禁止每卡散打 thumb sign-batch
    return safePreferred;
  }
  return '';
}

/**
 * 已签发的 rawUrl（非 preferred 字段）按请求 variant 派生可展示地址。
 * 与 {@link deriveVariantDisplayFromPreferred} 的差异：OSS thumb 不可冒充 full。
 */
function deriveSignedRawUrlForVariant(signedUrl, variant) {
  const safe = pickSafePreferredDisplaySrc(signedUrl);
  if (!safe) return '';
  const want = normalizeImageViewVariant(variant);
  if (isSignedViewPath(safe)) {
    // HMAC：full→thumb 追加 v=；已是目标 variant 则原样；请求 full 时去掉 v=
    if (!want) {
      try {
        const absolute = safe.startsWith('http://') || safe.startsWith('https://');
        const parsed = absolute ? new URL(safe) : new URL(safe, 'http://local');
        if (!parsed.searchParams.has('v')) {
          return absolute ? safe : `${parsed.pathname}${parsed.search}`;
        }
        parsed.searchParams.delete('v');
        return absolute ? parsed.toString() : `${parsed.pathname}${parsed.search}`;
      } catch {
        return safe;
      }
    }
    return appendViewVariant(safe, want) || safe;
  }
  if (isOssDirectDisplayUrl(safe)) {
    const embedded = embeddedSignedDisplayVariant(safe);
    // 未知 process：不当作可用展示
    if (embedded === '__process__') return '';
    // 已匹配（含 full 且无 process）
    if (embedded === want) return safe;
    // full（无 process）可作 thumb/preview/medium 占位
    if (!embedded && want) return safe;
    // thumb 不能当作 full
    return '';
  }
  return '';
}

/**
 * 手动裁剪等同路径覆盖后的展示缓存破坏（浏览器 + React src）。
 * 使用独立 {@code _mc} 查询参数，不走 {@code v=}（后者仅 thumb/preview/medium，且会影响后端图片处理）。
 * OSS 预签名不可改 query，原样返回，依赖 invalidate + 换签强制轮换。
 */
export function appendDisplayCacheBust(url, bustToken) {
  const raw = String(url || '').trim();
  const token = String(bustToken ?? '').trim();
  if (!raw || !token) return raw;
  if (raw.startsWith('data:') || raw.startsWith('blob:') || raw.startsWith('indexeddb://')) {
    return raw;
  }
  if (isOssDirectDisplayUrl(raw)) return raw;
  try {
    const absolute = raw.startsWith('http://') || raw.startsWith('https://');
    const parsed = absolute ? new URL(raw) : new URL(raw, 'http://local');
    if (parsed.searchParams.get('_mc') === token) return raw;
    parsed.searchParams.set('_mc', token);
    if (absolute) return parsed.toString();
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return raw;
  }
}

/** displayUrl/viewUrl 字段：data/blob/view/OSS 预签名可直出；受管 canonical / 裸 OSS 在 signed-only 下须走 sign-batch */
function pickSafePreferredDisplaySrc(preferred) {
  const s = normalizeUrlPath(String(preferred || '').trim());
  if (!s) return '';
  if (s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) {
    return s;
  }
  if (isSignedViewPath(s)) {
    if (isViewPathExpired(s)) return '';
    return toAbsoluteViewPath(s);
  }
  if (isOssDirectDisplayUrl(s)) {
    if (isViewPathExpired(s)) return '';
    return s;
  }
  if (shouldBlockBareUploadsDirectLink(s)) {
    return '';
  }
  // 裸 OSS 拦截；绝对 /uploads/（local identity）保留
  if (isBlockedSignedOnlyHttpDisplay(s)) {
    return '';
  }
  if (s.startsWith('/images/') || s.startsWith('/assets/')) {
    return s;
  }
  const abs = toAbsoluteViewPath(s);
  return sanitizeManagedDisplayResult(abs, s) || abs;
}

function normalizeDisplayCacheKey(canonical, variant) {
  const base = normalizeUrlPath(canonical);
  const v = normalizeImageViewVariant(variant);
  return v ? `${base}|v=${v}` : base;
}

function cacheDisplayUrl(
  canonical,
  viewPath,
  expSeconds,
  imageId,
  { notify = true, persist = true, variant } = {},
) {
  const key = normalizeDisplayCacheKey(canonical, variant);
  if (!key || !viewPath) return;
  const expMs = resolveViewPathExpiryMs(viewPath, expSeconds);
  displayCache.set(key, { viewPath, expMs });
  const id = imageId != null ? String(imageId).trim() : '';
  if (id) {
    const idKey = normalizeImageViewVariant(variant) ? `${id}|v=${normalizeImageViewVariant(variant)}` : id;
    imageIdDisplayCache.set(idKey, { canonical: key, viewPath, expMs });
  }
  if (notify) notifyDisplayCacheUpdate();
  if (persist) schedulePersistDisplayCacheToSessionStorage();
}

function readCachedDisplayUrl(canonical, variant) {
  const key = normalizeDisplayCacheKey(canonical, variant);
  const entry = displayCache.get(key);
  if (!entry) return '';
  const validUntil = resolveStoredEntryExpiryMs(entry);
  if (validUntil <= Date.now()) {
    displayCache.delete(key);
    return '';
  }
  entry.expMs = validUntil;
  return toAbsoluteViewPath(entry.viewPath);
}

function readCachedDisplayByImageId(imageId, variant) {
  const id = String(imageId || '').trim();
  if (!id) return '';
  const idKey = normalizeImageViewVariant(variant) ? `${id}|v=${normalizeImageViewVariant(variant)}` : id;
  const entry = imageIdDisplayCache.get(idKey);
  if (!entry) return '';
  const validUntil = resolveStoredEntryExpiryMs(entry);
  if (validUntil <= Date.now()) {
    imageIdDisplayCache.delete(idKey);
    return '';
  }
  entry.expMs = validUntil;
  return toAbsoluteViewPath(entry.viewPath);
}

function hydrateDisplayCacheFromSessionStorage() {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  const hydrateEntries = (entries) => {
    if (!Array.isArray(entries)) return;
    entries.forEach(([key, entry]) => {
      if (!key || !entry?.viewPath) return;
      const validUntil = resolveStoredEntryExpiryMs(entry);
      if (validUntil <= now) return;
      const normalized = { viewPath: entry.viewPath, expMs: validUntil };
      const existing = displayCache.get(key);
      if (!existing || existing.expMs < normalized.expMs) {
        displayCache.set(key, normalized);
      }
    });
  };
  try {
    const localRaw = localStorage.getItem(DISPLAY_CACHE_LOCAL_STORAGE_KEY);
    if (localRaw) hydrateEntries(JSON.parse(localRaw));
  } catch {
    // ignore corrupt cache
  }
  try {
    const raw = sessionStorage.getItem(DISPLAY_CACHE_STORAGE_KEY);
    if (raw) hydrateEntries(JSON.parse(raw));
  } catch {
    // ignore corrupt cache
  }
}

function schedulePersistDisplayCacheToSessionStorage() {
  if (typeof window === 'undefined') return;
  if (persistStorageTimer != null) return;
  persistStorageTimer = window.setTimeout(() => {
    persistStorageTimer = null;
    try {
      const now = Date.now();
      const entries = [];
      displayCache.forEach((entry, key) => {
        const validUntil = resolveViewPathExpiryMs(entry.viewPath, 0);
        if (validUntil > now) {
          entries.push([key, { viewPath: entry.viewPath, expMs: validUntil }]);
        }
      });
      entries.sort((a, b) => b[1].expMs - a[1].expMs);
      const payload = JSON.stringify(entries.slice(0, DISPLAY_CACHE_STORAGE_MAX));
      sessionStorage.setItem(DISPLAY_CACHE_STORAGE_KEY, payload);
      localStorage.setItem(DISPLAY_CACHE_LOCAL_STORAGE_KEY, payload);
    } catch {
      // quota exceeded etc.
    }
  }, 200);
}

/** sign-batch 或 API viewUrl 预热后通知订阅方重新同步读取缓存 */
export function subscribeDisplayCacheUpdate(listener) {
  cacheUpdateListeners.add(listener);
  return () => cacheUpdateListeners.delete(listener);
}

export function notifyDisplayCacheUpdate() {
  cacheUpdateListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  });
}

/**
 * 列表/API 已附带 viewUrl 时写入缓存，避免每张卡单独 sign 或异步等待。
 * @param {Array} items
 * @param {(item: object) => string} resolveRawUrl
 */
export function warmDisplayCacheFromRecords(items, resolveRawUrl = (item) => item?.url || item?.src || '') {
  let changed = false;
  for (const item of items || []) {
    const options = pickImageSignOptions(item);
    const raw = String(resolveRawUrl(item) || '').trim();
    const preferred =
      options.viewUrl
      || options.displayUrl
      || options.storedViewUrl
      || options.storedUrlDisplay;
    if (!raw || !preferred) continue;
    // 缓存键必须是 canonical；勿用带 process 的 OSS URL 当 key
    const cacheKey = toManagedCanonicalUploadsUrl(raw) || raw;
    // 已有未过期签名缓存时不覆盖，避免轮询刷新导致全表 img 重新请求
    if (!readCachedDisplayUrl(cacheKey)) {
      let displayUrl = resolveSignBatchDisplayUrl(cacheKey, preferred);
      if (!displayUrl) {
        const safePreferred = pickSafePreferredDisplaySrc(preferred);
        if (!safePreferred) continue;
        displayUrl = safePreferred;
      }
      const exp = parseDisplayUrlExpiry(preferred);
      cacheDisplayUrl(cacheKey, displayUrl, exp, item?.id, { notify: false, persist: false });
      changed = true;
      // HMAC：顺带写入 thumb/preview，列表 SignedImage(variant=thumb) 可同步命中
      if (isSignedViewPath(displayUrl)) {
        for (const thumbVariant of ['thumb', 'preview']) {
          if (readCachedDisplayUrl(cacheKey, thumbVariant)) continue;
          const derived = appendViewVariant(displayUrl, thumbVariant);
          if (!derived) continue;
          cacheDisplayUrl(cacheKey, derived, exp, item?.id, {
            notify: false,
            persist: false,
            variant: thumbVariant,
          });
        }
      }
    } else if (isSignedViewPath(pickSafePreferredDisplaySrc(preferred))) {
      // full 已缓存时仍补 thumb，避免上传后每卡散打 sign-batch
      const safePreferred = pickSafePreferredDisplaySrc(preferred);
      const exp = parseDisplayUrlExpiry(preferred);
      for (const thumbVariant of ['thumb', 'preview']) {
        if (readCachedDisplayUrl(cacheKey, thumbVariant)) continue;
        const derived = appendViewVariant(safePreferred, thumbVariant);
        if (!derived) continue;
        cacheDisplayUrl(cacheKey, derived, exp, item?.id, {
          notify: false,
          persist: false,
          variant: thumbVariant,
        });
        changed = true;
      }
    }
  }
  if (changed) {
    notifyDisplayCacheUpdate();
    schedulePersistDisplayCacheToSessionStorage();
  }
}

hydrateDisplayCacheFromSessionStorage();

const SIGN_BATCH_COALESCE_BASE_MS = 250;
const SIGN_BATCH_CHUNK_GAP_MS = 80;
const SIGN_BATCH_429_BACKOFF_BASE_MS = 1500;
const SIGN_BATCH_429_MAX_RETRIES = 4;
const SIGN_BATCH_MISS_BACKOFF_MS = 5 * 60 * 1000;
/** sign-batch 未产出有效 viewUrl 的 URL，短期内不再重试，避免 200 空响应时无限循环 */
const signBatchMissUntil = new Map();
let displayCacheMaintenanceStarted = false;

function canonicalManagedCacheKey(url) {
  const canonical = toManagedCanonicalUploadsUrl(url);
  if (canonical) return canonical;
  const s = normalizeUrlPath(String(url || '').trim());
  if (!s) return '';
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const parsed = new URL(s);
      if (parsed.pathname.startsWith('/uploads/')) return parsed.pathname;
      return s;
    } catch {
      return s;
    }
  }
  if (s.startsWith('uploads/')) return `/${s}`;
  return s;
}

function isSignBatchMissCached(url) {
  const key = canonicalManagedCacheKey(url);
  const until = signBatchMissUntil.get(key);
  if (!until) return false;
  if (Date.now() >= until) {
    signBatchMissUntil.delete(key);
    return false;
  }
  return true;
}

function markSignBatchMiss(url) {
  const key = canonicalManagedCacheKey(url);
  if (!key) return;
  signBatchMissUntil.set(key, Date.now() + SIGN_BATCH_MISS_BACKOFF_MS);
}

function lookupSignedViewInBatchMap(map, requestUrl) {
  if (!map || typeof map !== 'object') return '';
  const direct = map[requestUrl];
  if (direct) return direct;
  const key = canonicalManagedCacheKey(requestUrl);
  if (key !== requestUrl && map[key]) return map[key];
  for (const [candidate, viewPath] of Object.entries(map)) {
    if (canonicalManagedCacheKey(candidate) === key) return viewPath;
  }
  return '';
}

/** 扫描并清除已过期的签名缓存，通知订阅方重新 sign */
export function purgeExpiredDisplayCache() {
  const now = Date.now();
  let changed = false;
  displayCache.forEach((entry, key) => {
    if (resolveStoredEntryExpiryMs(entry) <= now) {
      displayCache.delete(key);
      changed = true;
    }
  });
  imageIdDisplayCache.forEach((entry, id) => {
    if (resolveStoredEntryExpiryMs(entry) <= now) {
      imageIdDisplayCache.delete(id);
      changed = true;
    }
  });
  if (changed) {
    notifyDisplayCacheUpdate();
    schedulePersistDisplayCacheToSessionStorage();
  }
  return changed;
}

function startDisplayCacheMaintenance() {
  if (displayCacheMaintenanceStarted || typeof window === 'undefined') return;
  displayCacheMaintenanceStarted = true;
  purgeExpiredDisplayCache();
  const scheduleNext = () => {
    const delay = DISPLAY_CACHE_MAINTENANCE_INTERVAL_MS
      + jitterMsForStableKey('ipe-display-cache-maint', DISPLAY_CACHE_MAINTENANCE_JITTER_MS);
    window.setTimeout(() => {
      purgeExpiredDisplayCache();
      scheduleNext();
    }, delay);
  };
  scheduleNext();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') purgeExpiredDisplayCache();
  });
}

startDisplayCacheMaintenance();

function parseViewExpiry(viewPath) {
  try {
    const query = viewPath.includes('?') ? viewPath.split('?')[1] : '';
    const params = new URLSearchParams(query);
    const exp = Number(params.get('e'));
    return Number.isFinite(exp) ? exp : 0;
  } catch {
    return 0;
  }
}

/** HMAC view 的 e=，或 OSS 预签名的 Expires=（均为 Unix epoch 秒） */
function parseDisplayUrlExpiry(url) {
  const path = String(url || '').trim();
  if (!path) return 0;
  if (isSignedViewPath(path)) return parseViewExpiry(path);
  if (!path.startsWith('http://') && !path.startsWith('https://')) return 0;
  try {
    const parsed = new URL(path);
    const expires = Number(parsed.searchParams.get('Expires') || parsed.searchParams.get('expires'));
    return Number.isFinite(expires) && expires > 0 ? expires : 0;
  } catch {
    return 0;
  }
}

/** view/OSS 直链的稳定资源键（忽略签名参数变化） */
function viewPathStableKeyForExpiry(viewPath) {
  return signedViewResourceKey(viewPath) || String(viewPath || '').trim();
}

function resolveViewRefreshLeadMs(viewPath) {
  const resourceKey = viewPathStableKeyForExpiry(viewPath);
  return VIEW_REFRESH_LEAD_BASE_MS + jitterMsForStableKey(resourceKey, VIEW_REFRESH_LEAD_JITTER_MS);
}

function resolveViewPathExpiryMs(viewPath, expSecondsHint) {
  let exp = Number(expSecondsHint);
  if (!Number.isFinite(exp) || exp <= 0) {
    exp = parseDisplayUrlExpiry(viewPath);
  }
  const refreshLead = resolveViewRefreshLeadMs(viewPath);
  if (Number.isFinite(exp) && exp > 1_000_000_000) {
    return exp * 1000 - refreshLead;
  }
  if (Number.isFinite(exp) && exp > 0) {
    return Date.now() + exp * 1000 - refreshLead;
  }
  const resourceKey = viewPathStableKeyForExpiry(viewPath);
  return Date.now() + ttlMsWithStableJitter(
    DISPLAY_CACHE_DEFAULT_TTL_MS,
    resourceKey,
    VIEW_REFRESH_LEAD_JITTER_MS,
  );
}

/**
 * 缓存条目的绝对失效时刻。
 * 有 Expires/e 时以 URL 为准；无 Expires 时必须以写入时的 entry.expMs 为准，
 * 禁止每次用 Date.now()+TTL 重算（否则永不过期）。
 */
function resolveStoredEntryExpiryMs(entry) {
  if (!entry?.viewPath) return 0;
  if (parseDisplayUrlExpiry(entry.viewPath) > 0) {
    return resolveViewPathExpiryMs(entry.viewPath, 0);
  }
  const stored = Number(entry.expMs);
  if (Number.isFinite(stored) && stored > 0) {
    return stored;
  }
  // 旧缓存缺 expMs：锚定一次，避免后续读取不断滑动延期
  const anchored = resolveViewPathExpiryMs(entry.viewPath, 0);
  entry.expMs = anchored;
  return anchored;
}

function findCachedExpMsForViewPath(viewPath) {
  const path = normalizeUrlPath(String(viewPath || '').trim());
  if (!path) return 0;
  const resourceKey = signedViewResourceKey(path);
  let best = 0;
  const consider = (entry) => {
    if (!entry?.viewPath) return;
    const entryPath = normalizeUrlPath(entry.viewPath);
    if (entryPath !== path && signedViewResourceKey(entryPath) !== resourceKey) return;
    const expMs = resolveStoredEntryExpiryMs(entry);
    if (expMs > best) best = expMs;
  };
  displayCache.forEach((entry) => consider(entry));
  imageIdDisplayCache.forEach((entry) => consider(entry));
  return best;
}

/** 距离签名展示 URL 客户端失效还剩多少毫秒（已含提前量）；支持 HMAC view 与 OSS Expires */
export function msUntilViewPathExpires(viewPath) {
  const path = String(viewPath || '').trim();
  if (!path || !isSignedDisplayUrl(path)) return 0;
  if (parseDisplayUrlExpiry(path) <= 0) {
    const cachedExp = findCachedExpMsForViewPath(path);
    if (cachedExp > 0) return Math.max(0, cachedExp - Date.now());
    // 尚未入缓存：按默认 TTL 调度一次刷新（不表示 URL 已过期）
    return ttlMsWithStableJitter(
      DISPLAY_CACHE_DEFAULT_TTL_MS,
      viewPathStableKeyForExpiry(path),
      VIEW_REFRESH_LEAD_JITTER_MS,
    );
  }
  return Math.max(0, resolveViewPathExpiryMs(path, 0) - Date.now());
}

/** 签名 view / canonical / OSS 地址的稳定资源键（忽略 e/s/Expires/Signature 等参数变化） */
export function signedViewResourceKey(url) {
  const s = normalizeUrlPath(String(url || '').trim());
  if (!s) return '';
  if (isSignedViewPath(s)) {
    try {
      const parsed = s.startsWith('http://') || s.startsWith('https://')
        ? new URL(s)
        : new URL(s, 'http://local');
      const objectKey = parsed.searchParams.get('k');
      const variant = parsed.searchParams.get('v');
      if (objectKey) {
        const base = decodeURIComponent(objectKey);
        return variant ? `${base}|v=${variant}` : base;
      }
    } catch {
      // ignore parse errors
    }
    return s.split('?')[0];
  }
  if (isUploadsStoragePath(s)) return stripUploadPrefix(s);
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const parsed = new URL(s);
      if (isUploadsStoragePath(parsed.pathname)) return stripUploadPrefix(parsed.pathname);
      if (isOssDisplayHost(parsed)) {
        const key = parsed.pathname.replace(/^\//, '');
        if (isManagedStorageUploadPath(key)) {
          const process = parsed.searchParams.get('x-oss-process') || '';
          const variant = Object.entries(IMAGE_VIEW_OSS_PROCESS)
            .find(([, style]) => style === process)?.[0] || '';
          return variant ? `${key}|v=${variant}` : key;
        }
      }
    } catch {
      // ignore parse errors
    }
  }
  return s.split('?')[0];
}

export function isSameDisplayImageResource(a, b) {
  const ka = signedViewResourceKey(a);
  const kb = signedViewResourceKey(b);
  if (!ka || !kb) return false;
  return ka === kb;
}

/**
 * invalidate 后须强制接受新签名的资源键（同 object key 覆盖写 / 手动裁剪）。
 * 常规 sign-batch 换签不写入此集合，避免列表无谓二次加载。
 */
const forceRotateResourceKeys = new Set();

function markForceRotateForUrl(url) {
  const key = signedViewResourceKey(url);
  if (key) forceRotateResourceKeys.add(key);
}

/** 是否因 invalidate 需强制轮换（不消费标记；供懒加载复用判断） */
export function hasForceRotateDisplayResource(url) {
  const key = signedViewResourceKey(url);
  return Boolean(key && forceRotateResourceKeys.has(key));
}

/** 测试 / 特殊清理 */
export function clearForceRotateDisplayResources() {
  forceRotateResourceKeys.clear();
}

/** 退出登录时清空签名图展示缓存，避免换号闪旧图 */
export function clearManagedImageDisplayCachesForLogout() {
  displayCache.clear();
  imageIdDisplayCache.clear();
  forceRotateResourceKeys.clear();
  try {
    sessionStorage.removeItem(DISPLAY_CACHE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(DISPLAY_CACHE_LOCAL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  try {
    notifyDisplayCacheUpdate();
  } catch {
    /* ignore */
  }
}

/**
 * 是否应将 img src 从 current 轮换为 next。
 * 同一资源且当前签名未过期时默认不轮换，避免 sign-batch / 缓存刷新导致二次加载；
 * 但 invalidate 后（同路径覆盖、手动裁剪）必须轮换，否则会卡在旧图。
 */
export function shouldRotateDisplayImageSrc(current, next) {
  const cur = String(current || '').trim();
  const nxt = String(next || '').trim();
  if (!nxt) return false;
  if (cur === nxt) return false;
  if (!cur) return true;
  if (!isSameDisplayImageResource(cur, nxt)) return true;
  const resourceKey = signedViewResourceKey(cur) || signedViewResourceKey(nxt);
  if (resourceKey && forceRotateResourceKeys.has(resourceKey)) {
    forceRotateResourceKeys.delete(resourceKey);
    return true;
  }
  if (isSignedDisplayUrl(cur) && msUntilViewPathExpires(cur) > 0) return false;
  return true;
}

function isViewPathExpired(viewPath) {
  const path = String(viewPath || '').trim();
  if (!path || !isSignedDisplayUrl(path)) return false;
  if (parseDisplayUrlExpiry(path) > 0) {
    return resolveViewPathExpiryMs(path, 0) <= Date.now();
  }
  // 无 Expires 的 OSS/CDN 公网直链：不按 URL epoch，按缓存写入时锚定的默认 TTL
  const cachedExp = findCachedExpMsForViewPath(path);
  if (cachedExp > 0) return cachedExp <= Date.now();
  return false;
}

/** 清除 canonical / imageId 的签名缓存（410 过期后强制 re-sign） */
export function invalidateManagedDisplayCache(rawUrl, options = {}) {
  const trimmed = normalizeUrlPath(String(rawUrl || '').trim());
  const variant = normalizeImageViewVariant(options.variant);
  if (trimmed) {
    markForceRotateForUrl(trimmed);
    const canonical = toManagedCanonicalUploadsUrl(trimmed) || trimmed;
    markForceRotateForUrl(canonical);
    const bases = [...new Set([normalizeUrlPath(trimmed), normalizeUrlPath(canonical)].filter(Boolean))];
    for (const base of bases) {
      if (variant) {
        displayCache.delete(normalizeDisplayCacheKey(base, variant));
      } else {
        displayCache.delete(base);
        for (const key of [...displayCache.keys()]) {
          if (key.startsWith(`${base}|v=`)) displayCache.delete(key);
        }
      }
    }
  }
  const id = options.imageId != null ? String(options.imageId).trim() : '';
  if (id) {
    if (variant) {
      imageIdDisplayCache.delete(`${id}|v=${variant}`);
    } else {
      imageIdDisplayCache.delete(id);
      for (const key of [...imageIdDisplayCache.keys()]) {
        if (key.startsWith(`${id}|v=`)) imageIdDisplayCache.delete(key);
      }
    }
  }
  notifyDisplayCacheUpdate();
  schedulePersistDisplayCacheToSessionStorage();
}

function stripExpiredViewOptions(options = {}) {
  const next = { ...options };
  for (const key of [
    'displayUrl',
    'viewUrl',
    'storedViewUrl',
    'storedUrlDisplay',
    'primaryImageViewUrl',
    'primaryImageUrlDisplay',
  ]) {
    const value = String(next[key] || '').trim();
    if (value && isSignedDisplayUrl(value) && isViewPathExpired(value)) {
      delete next[key];
    }
  }
  return next;
}

/** 410 / 加载失败后刷新签名读图地址 */
export async function refreshSignedDisplayUrl(rawUrl, options = {}) {
  const trimmed = normalizeUrlPath(String(rawUrl || '').trim());
  const nextOptions = stripExpiredViewOptions(options);
  invalidateManagedDisplayCache(trimmed, nextOptions);
  const resolved = await resolveSignedDisplayUrl(trimmed, nextOptions);
  return sanitizeManagedDisplayResult(resolved, trimmed) || resolved || '';
}

export async function ensureDisplayImageUrls(urls, options = {}) {
  const variant = normalizeImageViewVariant(options.variant);
  const list = Array.isArray(urls) ? urls : [];
  const need = list
    .map((u) => String(u || '').trim())
    .filter((u) => {
      if (!u || !isManagedStorageUrl(u)) return false;
      // 仅当已是「匹配请求 variant」的未过期签名地址时才 skip；
      // 带 x-oss-process 的 thumb 不能在无 variant（full）请求下 skip
      if (isReadySignedDisplayForVariant(u, variant)) return false;
      return true;
    })
    .map((u) => toManagedCanonicalUploadsUrl(u) || u)
    .filter((u, idx, arr) => arr.indexOf(u) === idx)
    .filter((u) => !readCachedDisplayUrl(u, variant))
    .filter((u) => !isSignBatchMissCached(u));
  if (!need.length) return;

  need.forEach((url) => signCoalesce.queue.add(encodeSignQueueEntry(url, variant)));
  return new Promise((resolve) => {
    signCoalesce.waiters.push(resolve);
    scheduleCoalescedSignBatch();
  });
}

/** 列表/弹窗数据到达后批量预热签名缓存，供 SignedLazyImage 同步命中 */
export function prefetchDisplayUrlsForListItems(
  items,
  resolveRawUrl = (item) => item?.url || item?.src || item?.thumbnailUrl || '',
  options = {},
) {
  const variant = normalizeImageViewVariant(options.variant ?? 'thumb');
  warmDisplayCacheFromRecords(items, resolveRawUrl);
  const urls = [];
  for (const item of items || []) {
    const raw = String(resolveRawUrl(item) || '').trim();
    if (!raw || raw.startsWith('data:') || raw.startsWith('blob:') || raw.startsWith('indexeddb://')) {
      continue;
    }
    if (!isManagedStorageUrl(raw)) continue;
    urls.push(toManagedCanonicalUploadsUrl(raw) || raw);
  }
  const unique = [...new Set(urls)];
  if (!unique.length) return Promise.resolve();
  return ensureDisplayImageUrls(unique, { variant });
}

function isSignBatchRateLimitedError(ex) {
  const status = Number(ex?.status);
  if (status === 429) return true;
  const message = String(ex?.message || ex?.code || '');
  return /过于频繁|TOO_MANY_REQUESTS|429/i.test(message);
}

function scheduleCoalescedSignBatch() {
  if (signCoalesce.timer != null) return;
  const sampleUrl = signCoalesce.queue.values().next().value || 'sign-batch';
  const delay = SIGN_BATCH_COALESCE_BASE_MS + staggerDelayMsForKey(sampleUrl, 200);
  signCoalesce.timer = window.setTimeout(() => {
    signCoalesce.timer = null;
    void flushCoalescedSignBatch();
  }, delay);
}

async function flushCoalescedSignBatch() {
  if (signCoalesce.inflight) {
    await signCoalesce.inflight;
    if (signCoalesce.queue.size > 0) {
      scheduleCoalescedSignBatch();
    }
    return;
  }

  signCoalesce.inflight = (async () => {
    let rateLimitRetries = 0;
    while (signCoalesce.queue.size > 0) {
      const batch = [];
      for (const entry of signCoalesce.queue) {
        batch.push(entry);
        signCoalesce.queue.delete(entry);
        if (batch.length >= SIGN_BATCH_MAX) break;
      }
      // 同一批次按 variant 分组（sign-batch 单次只带一个 variant）
      const byVariant = new Map();
      batch.forEach((entry) => {
        const decoded = decodeSignQueueEntry(entry);
        const v = normalizeImageViewVariant(decoded.variant);
        if (!byVariant.has(v)) byVariant.set(v, []);
        byVariant.get(v).push(decoded.url);
      });

      for (const [variant, variantUrls] of byVariant) {
        const need = variantUrls.filter((u) => !readCachedDisplayUrl(u, variant));
        if (need.length === 0) continue;
        try {
          const res = await imageAccessAPI.signBatch(need, variant ? { variant } : {});
          rateLimitRetries = 0;
          const map = res?.urls && typeof res.urls === 'object' ? res.urls : {};
          let cachedAny = false;
          need.forEach((requestUrl) => {
            const batchViewPath = lookupSignedViewInBatchMap(map, requestUrl);
            const displayUrl = resolveSignBatchDisplayUrl(requestUrl, batchViewPath);
            if (!displayUrl) {
              markSignBatchMiss(requestUrl);
              return;
            }
            const exp = parseDisplayUrlExpiry(batchViewPath);
            cacheDisplayUrl(requestUrl, displayUrl, exp, undefined, {
              notify: false,
              persist: false,
              variant,
            });
            signBatchMissUntil.delete(canonicalManagedCacheKey(requestUrl));
            cachedAny = true;
          });
          if (cachedAny) {
            schedulePersistDisplayCacheToSessionStorage();
            notifyDisplayCacheUpdate();
          }
        } catch (ex) {
          if (isSignBatchRateLimitedError(ex) && rateLimitRetries < SIGN_BATCH_429_MAX_RETRIES) {
            rateLimitRetries += 1;
            need.forEach((url) => signCoalesce.queue.add(encodeSignQueueEntry(url, variant)));
            const backoff = SIGN_BATCH_429_BACKOFF_BASE_MS * rateLimitRetries
              + jitterMsForStableKey(need[0] || 'sign-batch-429', 1200);
            await new Promise((resolve) => {
              window.setTimeout(resolve, backoff);
            });
            continue;
          }
          console.warn('[imageDisplayAccess] sign-batch 失败，将回退原始地址展示', ex);
        }
      }
      if (signCoalesce.queue.size > 0) {
        const gap = SIGN_BATCH_CHUNK_GAP_MS + jitterMsForStableKey(batch[0] || 'chunk', 480);
        await new Promise((resolve) => {
          window.setTimeout(resolve, gap);
        });
      }
    }
  })();

  try {
    await signCoalesce.inflight;
  } finally {
    signCoalesce.inflight = null;
    const waiters = signCoalesce.waiters.splice(0);
    waiters.forEach((resolve) => resolve());
    if (signCoalesce.queue.size > 0) {
      scheduleCoalescedSignBatch();
    }
  }
}

/** 受管存储：优先 HMAC/预签名；legacy 裸 /uploads/ 升为同域绝对地址，避免清空后永久白底 */
export function sanitizeManagedDisplayResult(resolved, canonical) {
  const value = String(resolved || '').trim();
  if (!value) return '';
  const raw = normalizeUrlPath(canonical);
  if (
    isSignedViewEnabled()
    && raw
    && isUploadsStoragePath(raw)
    && !value.startsWith('/api/storage/images/view')
    && !value.startsWith('http://')
    && !value.startsWith('https://')
    && value === raw
  ) {
    // sign-batch 在 signed-only=false 时会回传与 canonical 相同的裸路径：升为绝对直链供 <img>
    return resolvePublicManagedUploadsUrl(raw) || value;
  }
  // 裸 OSS 拒绝；绝对 /uploads/（local）与 HMAC/预签名保留
  if (isBlockedSignedOnlyHttpDisplay(value)) {
    return '';
  }
  return value;
}

/**
 * 同步解析可安全用于 <img src> 的地址：优先 viewUrl/缓存，受管存储无签名时不回退 canonical。
 * 本地 blob/data 预览优先于签图缓存，避免上传后仍批量打 view 触发 429。
 */
export function resolveImmediateSignedSrc(rawUrl, options = {}) {
  const { imageId } = options;
  const variant = normalizeImageViewVariant(options.variant);

  const preferredEarly = pickPreferredDisplayField(options);
  if (preferredEarly) {
    const localPreferred = normalizeUrlPath(String(preferredEarly).trim());
    if (
      localPreferred.startsWith('data:')
      || localPreferred.startsWith('blob:')
      || localPreferred.startsWith('indexeddb://')
    ) {
      return localPreferred;
    }
  }

  const byImageId = readCachedDisplayByImageId(imageId, variant);
  if (byImageId) return byImageId;

  const trimmed = normalizeUrlPath(rawUrl);
  const canonical = trimmed ? (toManagedCanonicalUploadsUrl(trimmed) || trimmed) : '';
  // 先读 variant 缓存（列表 prefetch 写入的真·thumb），再回退 preferred，避免 OSS full 占位挡住升级
  if (canonical && (isManagedStorageUrl(canonical) || isManagedStorageUrl(trimmed))) {
    const cached = readCachedDisplayUrl(canonical, variant);
    if (cached) return cached;
  }

  const preferred = preferredEarly || pickPreferredDisplayField(options);
  if (preferred) {
    const derived = deriveVariantDisplayFromPreferred(preferred, variant);
    if (derived) return derived;
  }

  if (!trimmed) return '';
  if (
    trimmed.startsWith('data:')
    || trimmed.startsWith('blob:')
    || trimmed.startsWith('indexeddb://')
  ) {
    return trimmed;
  }
  if (isReadySignedDisplayForVariant(trimmed, variant)) {
    return isSignedViewPath(trimmed) ? toAbsoluteViewPath(trimmed) : trimmed;
  }
  // rawUrl 本身已是签发地址（列表 previewUrlMap / 详情 enrich 常给 full），
  // 请求 thumb 时不可因 variant 不匹配而清空——否则会误打 sign-batch（归属失败则封面空白）。
  // 线下 HMAC：追加 v=thumb；线上 OSS full 直链：可复用做 thumb 占位；OSS thumb 不可冒充 full。
  if (isSignedDisplayUrl(trimmed) && !isViewPathExpired(trimmed)) {
    const derivedFromRaw = deriveSignedRawUrlForVariant(trimmed, variant);
    if (derivedFromRaw) return derivedFromRaw;
  }
  if (canonical && (isManagedStorageUrl(canonical) || isManagedStorageUrl(trimmed))) {
    // 本地绝对 uploads 已可加载，勿因 signed-only 清空（与线上 OSS 预签名并列的第三种形态）
    if (isAbsoluteLocalUploadsDisplayUrl(trimmed)) return trimmed;
    if (isSignedViewEnabled()) return '';
    return isUploadsStoragePath(canonical) ? canonical : trimmed;
  }
  if (shouldBlockBareUploadsDirectLink(trimmed)) return '';
  return trimmed;
}

export function resolveDisplayImageUrl(rawUrl, options = {}) {
  const {
    displayUrl,
    urlDisplay,
    viewUrl,
    storedUrlDisplay,
    storedViewUrl,
    primaryImageUrlDisplay,
    primaryImageViewUrl,
  } = options;
  const preferred =
    displayUrl ||
    urlDisplay ||
    viewUrl ||
    storedUrlDisplay ||
    storedViewUrl ||
    primaryImageUrlDisplay ||
    primaryImageViewUrl ||
    options.initialImageUrlDisplay ||
    options.finalImageUrlDisplay ||
    options.outputImageUrlDisplay ||
    options.inputImageUrlDisplay ||
    options.outputVideoUrlDisplay ||
    options.videoUrlDisplay ||
    options.outputVideoPosterUrlDisplay ||
    options.previewDisplayUrl ||
    options.previewViewUrl;
  if (preferred) {
    const safePreferred = pickSafePreferredDisplaySrc(preferred);
    if (safePreferred) return safePreferred;
  }
  const trimmed = normalizeUrlPath(rawUrl);
  if (!trimmed) return '';
  if (isReadySignedDisplayForVariant(trimmed, '')) {
    return isSignedViewPath(trimmed) ? toAbsoluteViewPath(trimmed) : trimmed;
  }
  const canonical = toManagedCanonicalUploadsUrl(trimmed) || trimmed;
  if (isManagedStorageUrl(canonical) || isManagedStorageUrl(trimmed)) {
    const cached = readCachedDisplayUrl(canonical);
    if (cached) return cached;
    if (isAbsoluteLocalUploadsDisplayUrl(trimmed)) return trimmed;
    if (isSignedViewEnabled()) return '';
    return isUploadsStoragePath(canonical) ? canonical : trimmed;
  }
  if (shouldBlockBareUploadsDirectLink(trimmed)) return '';
  return trimmed;
}

/** 异步解析受管存储签名读图地址（展示前应先 await 本函数） */
export async function resolveSignedDisplayUrl(rawUrl, options = {}) {
  const variant = normalizeImageViewVariant(options.variant);
  const trimmed = normalizeUrlPath(rawUrl);
  const canonical = trimmed ? (toManagedCanonicalUploadsUrl(trimmed) || trimmed) : '';

  if (canonical && (isManagedStorageUrl(canonical) || isManagedStorageUrl(trimmed))) {
    const cached = readCachedDisplayUrl(canonical, variant);
    if (cached) return cached;
  }

  const preferred = pickPreferredDisplayField(options);
  if (preferred) {
    const derived = deriveVariantDisplayFromPreferred(preferred, variant);
    if (derived) {
      // OSS/HMAC 已有可用展示地址：不再 per-card ensure；列表级 prefetch 负责 thumb 升级
      return derived;
    }
  }

  if (!trimmed) return '';
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:') || trimmed.startsWith('indexeddb://')) {
    return trimmed;
  }
  if (isReadySignedDisplayForVariant(trimmed, variant)) {
    return isSignedViewPath(trimmed) ? toAbsoluteViewPath(trimmed) : trimmed;
  }
  // 与 resolveImmediateSignedSrc 一致：已签发 rawUrl 直接派生 variant，避免 sign-batch 归属失败导致列表封面空白
  if (isSignedDisplayUrl(trimmed) && !isViewPathExpired(trimmed)) {
    const derivedFromRaw = deriveSignedRawUrlForVariant(trimmed, variant);
    if (derivedFromRaw) return derivedFromRaw;
  }
  if (isManagedStorageUrl(canonical) || isManagedStorageUrl(trimmed)) {
    await ensureDisplayImageUrls([canonical], { variant });
    const cached = readCachedDisplayUrl(canonical, variant);
    if (cached) return cached;
    if (isAbsoluteLocalUploadsDisplayUrl(trimmed)) return trimmed;
    if (isSignedViewEnabled()) return '';
  }
  const fallback = resolveDisplayImageUrl(canonical, options);
  const safe = sanitizeManagedDisplayResult(fallback, canonical);
  if (safe) return safe;
  if (shouldBlockBareUploadsDirectLink(canonical) || shouldBlockBareUploadsDirectLink(trimmed)) return '';
  return fallback || (isUploadsStoragePath(canonical) ? '' : trimmed);
}

export {
  isManagedStorageUrl,
  isOssManagedUrl,
  isUploadsStoragePath,
  toAbsoluteViewPath,
};
